import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { useParams, useSearchParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { z } from "zod";
import { Button } from "components/ui/button/Button";
import Form from "components/ui/form/Form";
import { FormInput } from "components/ui/form/FormInput";
import { Select } from "components/ui/select/Select";
import PhoneNumberInput from "components/ui/PhoneNumberInput/PhoneNumberInput";
import Footer from "components/footer/Footer";
import { emailAddress, firstname, lastname } from "utilities/formSchemas";
import useDesktopChecker from "hooks/useDesktopChecker";
import { useRobotsMetaTag } from "hooks/useRobotsMetaTag";
import "./selfRegistration.scss";
import { Consent } from "components/consent/Consent";
import LoanOfficerCardV2 from "components/v2/loan-officer-card/LoanOfficerCardV2";
import LoanOfficerCard from "components/v2/loan-officer-card/LoanOfficerCard";
import {
    LoanOfficerDetail,
    useGetLoanOfficerByLoIdQuery,
    useGetLoanOfficerByNmlsQuery,
} from "pages/application/applicationService";
import homeGraphic from "assets/images/HomeGraphic.png";
import {
    SelfRegisterRequest,
    useGetUsStatesQuery,
    usePostSelfRegistrationMutation,
} from "pages/registration/registrationService";
import { LoadingOverlay } from "components/ui/loading-spinners";
import {
    trackFSVisitedSelfRegistration,
    trackFSCompletedSelfRegistration,
    trackFSRejectedSelfRegistration,
    setFSUserProperties,
} from "utilities/full-story";
import LPSThankYou from "pages/LpsThankyou/LpsThankyou";
import { useLoProfileCardTracking } from "hooks/useLoProfileCardTracking";
import { isLoLookupNotFound, redirectToLoSearch } from "utilities/loanOfficerDisplay";
import { getPrivacyAndTermsOfUseConsent } from "utilities/consentLegalContent";

const LOAN_PURPOSE_OPTIONS = ["Purchase", "Refinance"];

const formSchema = z.object({
    firstName: firstname,
    lastName: lastname,
    email: emailAddress,
    phone: z
        .string({ required_error: "Please enter phone number" })
        .min(1, { message: "Please enter phone number" })
        .refine((val) => val.replace(/\D/g, "").length === 10, {
            message: "Please enter a valid phone number",
        }),
    loanPurpose: z
        .string({ required_error: "Please select loan purpose" })
        .min(1, { message: "Please select loan purpose" }),
    subjectPropertyState: z
        .string({ required_error: "Please select subject property state" })
        .min(1, { message: "Please select subject property state" }),
});

type SelfRegistrationFormValues = z.infer<typeof formSchema>;

const renderDesktopLoPanel = (
    loanOfficer: LoanOfficerDetail | null,
    isLoadingLO: boolean,
    onHeadshotError: () => void
) => {
    if (loanOfficer) {
        return <LoanOfficerCardV2 loInfo={loanOfficer} onHeadshotError={onHeadshotError} />;
    }

    if (isLoadingLO) {
        return <LoanOfficerCardV2 isLoading />;
    }

    return (
        <div className="self-registration__default-content">
            <div className="self-registration__message">
                <div className="self-registration__message-heading">
                    <h1>Better Path to Homeownership</h1>
                </div>
                <div className="self-registration__message-sub-heading">
                    <h2>Seamless, Secure, and Built for You</h2>
                </div>
                <ul className="self-registration__message-list">
                    <li>Complete the questionnaire</li>
                    <li>Upload required docs</li>
                    <li>Talk to a loan officer</li>
                </ul>
            </div>
            <div className="self-registration__home-graphic-container">
                <img src={homeGraphic} alt="login-graphic" className="self-registration__home-graphic-container-image" />
            </div>
        </div>
    );
};

export default function SelfRegistration() {
    useRobotsMetaTag("noindex,nofollow");
    const [searchParams] = useSearchParams();
    const [error, setError] = useState(0);
    const [showLoading, setShowLoading] = useState(false);
    const isDesktop = useDesktopChecker();
    const { loNmlsNumber, branchId } = useParams();
    const sourceCodeId = searchParams.get("src") || branchId || null;
    const loanOfficerId = searchParams.get("log") || null;

    const {
        handleSubmit,
        control,
        register,
        watch,
        formState: { errors, isValid },
    } = useForm<SelfRegistrationFormValues>({
        resolver: zodResolver(formSchema),
        mode: "onChange",
        defaultValues: {
            firstName: "",
            lastName: "",
            email: "",
            phone: "",
            loanPurpose: "",
            subjectPropertyState: "",
        },
    });

    const [sendRequest] = usePostSelfRegistrationMutation();

    const hasNmlsLookup = !!loNmlsNumber;
    const hasLoIdLookup = !!loanOfficerId && !hasNmlsLookup;

    const { data: loDataByNmls, isLoading: isLoadingLOByNmls, error: loErrorByNmls } = useGetLoanOfficerByNmlsQuery(
        loNmlsNumber ?? "",
        { skip: !hasNmlsLookup },
    );
    const { data: loDataByLoId, isLoading: isLoadingLOByLoId, error: loErrorByLoId } = useGetLoanOfficerByLoIdQuery(
        loanOfficerId ?? "",
        { skip: !hasLoIdLookup },
    );

    const loData = hasNmlsLookup ? loDataByNmls : loDataByLoId;
    const isLoadingLO = hasNmlsLookup ? isLoadingLOByNmls : isLoadingLOByLoId;
    const loanOfficerError = hasNmlsLookup ? loErrorByNmls : loErrorByLoId;

    const { data: defaultStates, isLoading: isLoadingDefaultStates } = useGetUsStatesQuery(loData?.userId, {
        skip: isLoadingLO,
    }) as { data: string[] | undefined; isLoading: boolean };

    const isLoadingStates = isLoadingLO || isLoadingDefaultStates;
    const stateOptions: string[] = isLoadingStates ? [] : defaultStates ?? [];

    const hasLoLookup = hasNmlsLookup || hasLoIdLookup;
    const loanOfficer = loData?.fullName && loData.fullName !== "" && loData.fullName !== "Call Queue" ? loData : null;

    const [loHeadshotLoadError, setLoHeadshotLoadError] = useState(false);
    useEffect(() => {
        setLoHeadshotLoadError(false);
    }, [loanOfficer?.photoImageUrl]);

    useLoProfileCardTracking({
        surface: "form",
        loInfo: loanOfficer,
        isLoading: isLoadingLO,
        error: loanOfficerError,
        headshotLoadError: loHeadshotLoadError,
    });

    const shouldRedirectToLoSearch =
        hasNmlsLookup && isLoLookupNotFound(isLoadingLOByNmls, loErrorByNmls);

    useEffect(() => {
        if (shouldRedirectToLoSearch) {
            redirectToLoSearch();
        }
    }, [shouldRedirectToLoSearch]);

    const buildTrackingProperties = (formData?: { loanPurpose?: string; subjectPropertyState?: string }) => {
        let salesChannel = "Unknown"; 
        
        if (loanOfficer?.domains?.[0]?.domainName) {
            const firstDomainName = loanOfficer.domains[0].domainName.trim().toLowerCase();
            
            if (firstDomainName === "retail") {
                salesChannel = "OLA";
            } else if (firstDomainName === "cd") {
                salesChannel = "ILA";
            }
        }
        
        return {
            salesChannel,
            loFullName: loanOfficer?.fullName,
            loNMLS: loanOfficer?.nmlsLicenseNumber,
            loanPurpose: formData?.loanPurpose,
            state: formData?.subjectPropertyState,
        };
    };

    useEffect(() => {
        if (hasLoLookup && isLoadingLO) return;

        if (hasLoLookup && !loData) return;

        trackFSVisitedSelfRegistration(buildTrackingProperties());
    }, [hasLoLookup, isLoadingLO, loData, sourceCodeId, loanOfficer]);

    const onSubmit = (data: SelfRegisterRequest) => {
        setShowLoading(true);

        sendRequest(data)
            .unwrap()
            .then((redirectUrl: string) => {
                if (!redirectUrl || redirectUrl.trim().length === 0) {
                    setShowLoading(false);
                    setError(400);
                    trackFSRejectedSelfRegistration({
                        ...buildTrackingProperties({ 
                            loanPurpose: data.loanPurpose, 
                            subjectPropertyState: data.subjectPropertyState 
                        }),
                        rejectedReason: "Empty redirect URL returned",
                        FailureCode: 400,
                    });
                    return;
                }

                const cleanUrl = redirectUrl.trim();

                // Validate URL format - backend guarantees https:// protocol
                try {
                    new URL(cleanUrl);
                } catch {
                    // URL constructor throws TypeError for invalid URLs
                    setShowLoading(false);
                    setError(400);
                    trackFSRejectedSelfRegistration({
                        ...buildTrackingProperties({ 
                            loanPurpose: data.loanPurpose, 
                            subjectPropertyState: data.subjectPropertyState 
                        }),
                        rejectedReason: "Invalid redirect URL format",
                        FailureCode: 400,
                    });
                    return;
                }

                setFSUserProperties(data.email, data.lastName.toLowerCase());

                trackFSCompletedSelfRegistration({
                    ...buildTrackingProperties({ 
                        loanPurpose: data.loanPurpose, 
                        subjectPropertyState: data.subjectPropertyState 
                    }),
                });

                setTimeout(() => {
                    try {
                        globalThis.location.assign(cleanUrl);
                    } catch {
                        // Fallback to href if assign method fails
                        globalThis.location.href = cleanUrl;
                    }
                }, 100);
            })
            .catch((error_: { status?: number; data?: unknown; error?: unknown }) => {
                setShowLoading(false);

                let rejectedReason: string;

                if (error_.error && !error_.status) {
                    // Network/connectivity error without status code
                    setError(500);
                    rejectedReason = "Network/connectivity error";
                } else {
                    const status = error_.status;
                    
                    switch (status) {
                        case 400:
                        case 401:
                            setError(400);
                            rejectedReason = `Client error - Status ${status}`;
                            break;
                        case 500:
                        default:
                            setError(500);
                            rejectedReason = `Server error - Status ${status ?? "Unknown"}`;
                            break;
                    }
                }

                trackFSRejectedSelfRegistration({
                    ...buildTrackingProperties({ 
                        loanPurpose: data.loanPurpose, 
                        subjectPropertyState: data.subjectPropertyState 
                    }),
                    rejectedReason,
                    FailureCode: error_.status ?? "unknown",
                });
            });
    };

    const formContent = (
        <Form
            onSubmit={handleSubmit((data) => {
                onSubmit({
                    email: data.email,
                    firstName: data.firstName,
                    lastName: data.lastName,
                    phone: data.phone,
                    loanPurpose: data.loanPurpose,
                    subjectPropertyState: data.subjectPropertyState,
                    sourceCodeId: sourceCodeId ? Number.parseInt(sourceCodeId) : undefined,
                    loanOfficerId: loanOfficerId ?? loData?.userId,
                    referringUrl: globalThis.location.href,
                    postUrl: globalThis.location.href,
                });
            })}
            formClassName="form self-registration__form-inner"
        >
            <div className="self-registration__grid">
                <div className="self-registration__grid-item">
                    <FormInput
                        formHandle={register("firstName")}
                        inputProps={{
                            name: "firstName",
                            placeholder: "Enter first name",
                            label: "First Name",
                            errorMessage: errors.firstName?.message,
                            excludeFromTranslation: false,
                        }}
                    />
                </div>
                <div className="self-registration__grid-item">
                    <FormInput
                        formHandle={register("lastName")}
                        inputProps={{
                            name: "lastName",
                            placeholder: "Enter last name",
                            label: "Last Name",
                            errorMessage: errors.lastName?.message,
                            excludeFromTranslation: false,
                        }}
                    />
                </div>
                <div className="self-registration__grid-item">
                    <Controller
                        name="loanPurpose"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                            <Select
                                options={LOAN_PURPOSE_OPTIONS}
                                placeholder="Select"
                                label="Loan Purpose"
                                errorMessage={errors.loanPurpose?.message}
                                onChange={onChange}
                                initialValue={value}
                            />
                        )}
                    />
                </div>
                <div className="self-registration__grid-item">
                    <Controller
                        name="subjectPropertyState"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                            <Select
                                options={stateOptions}
                                placeholder="Select"
                                label="Subject Property State"
                                errorMessage={errors.subjectPropertyState?.message}
                                onChange={onChange}
                                initialValue={value}
                            />
                        )}
                    />
                </div>
                <div className="self-registration__grid-item">
                    <FormInput
                        formHandle={register("email")}
                        inputProps={{
                            name: "email",
                            placeholder: "Enter email",
                            label: "Email Address",
                            errorMessage: errors.email?.message,
                            excludeFromTranslation: false,
                        }}
                    />
                </div>
                <div className="self-registration__grid-item">
                    <div className={(watch("phone") ?? "").replace(/\D/g, "").length === 0 ? "phone-input--empty" : ""}>
                        <Controller
                            name="phone"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                                <PhoneNumberInput
                                    onChange={(newValue: string) => {
                                        onChange(newValue);
                                    }}
                                    value={value}
                                    errorMessage={errors.phone?.message}
                                    label="Phone Number"
                                    placeholder={"(XXX) XXX-XXXX"}
                                />
                            )}
                        />
                    </div>
                </div>
            </div>
            <Button
                title="Continue"
                className="self-registration__submit-btn"
                disabled={!isValid || showLoading}
            />
        </Form>
    );
    if (showLoading)
        return (
            <LoadingOverlay
                text="Setting up your profile"
                subtext="This will take a moment"
            />
        );
    if (error !== 0) {
        return <LPSThankYou loanOfficer={loanOfficer || undefined} />;
    }
    return (
        <>
            <div className="self-registration">
                <div className="self-registration__container">
                    <div className="self-registration__form">
                        <p className="self-registration__header">
                            We need a few details to get started
                        </p>
                        {loanOfficer && !isDesktop && (
                            <div className="self-registration__loan-officer-info">
                                <p className="self-registration__loan-officer-info-text">You've been invited to start your application.</p>
                                <LoanOfficerCard
                                    loInfo={loanOfficer}
                                    hideLabel
                                    onHeadshotError={() => setLoHeadshotLoadError(true)}
                                />
                            </div>
                        )}
                        {!loanOfficer && !isDesktop && isLoadingLO && (
                            <div className="self-registration__loan-officer-info">
                                <LoanOfficerCard isLoading hideLabel />
                            </div>
                        )}
                        {formContent}
                        <div className="self-registration__consent">
                            <Consent text={getPrivacyAndTermsOfUseConsent()} />
                        </div>
                    </div>
                    {isDesktop && (
                        <div className="self-registration__loan-officer">
                            {renderDesktopLoPanel(loanOfficer, isLoadingLO, () => setLoHeadshotLoadError(true))}
                        </div>
                    )}
                </div>
            </div>
            <div className="self-registration__footer-section">
                <Footer />
            </div>
        </>
    );
}
